/**
 * Master dictionary. Every other locale is typed as `Messages`, so a missing or
 * misspelled key is a compile error. Rich text supports **bold** and line breaks (\n).
 */
import { enTalks } from './en.talks';
import { enExtra } from './en.extra';
import { enPlaces } from './en.places';
import { enArcade } from './en.arcade';
import { enTreasure } from './en.treasure';
import { enTalks3 } from './en.talks3';
import { enGuide } from './en.guide';
import { enWorld3 } from './en.world3';
import { enTrips } from './en.trips';
import { enSky } from './en.sky';
import { enFamily } from './en.family';
import { enClassroom } from './en.classroom';
import { enLife } from './en.life';
import { enXinyi } from './en.xinyi';
import { enXimen } from './en.ximen';
import { enCks } from './en.cks';
import { enHlcity } from './en.hlcity';
import { enTaroko } from './en.taroko';
import { enStory } from './en.story';
import { enAirport } from './en.airport';
import { enKaohsiung } from './en.kaohsiung';
import { enTainan } from './en.tainan';
import { enBarcelona } from './en.barcelona';

export const en = {
  meta: {
    title: 'Campus Life Adventure: Don’t Collapse Today!',
  },

  start: {
    title: 'Campus Life Adventure',
    tagline: 'Don’t collapse today!',
    blurb: 'A week in the life of a middle-school peer helper: school, family, friends — and a lot of tea.',
    language: 'Language',
    look: 'Your look',
    pet: 'A pet to keep you company',
    birthdays: 'Birthdays (fill in your own)',
    you: 'You',
    mom: 'Mom',
    dad: 'Dad',
    emma: 'Emma',
    month: 'M',
    day: 'D',
    newGame: 'New game',
    continue: 'Continue',
    continueInfo: (week: number, day: string, time: string) => `Week ${week} · ${day} · ${time}`,
    confirmNew: 'Start a new game? Your saved progress will be lost.',
    controls: 'Move: arrows / WASD / tap · Interact: Space / E · Jump: Space / X / 🦘 · Map: M · Quests: Q · Menu: Esc',
  },

  looks: ['Short brown hair', 'Long black hair', 'Curly red hair', 'Blond ponytail'],

  pets: {
    rabbit: 'Bunny',
    cat: 'Cat',
    dog: 'Shiba',
    hedgehog: 'Hedgehog',
    hamster: 'Hamster',
    croc: 'Croc',
    fish: 'Fish',
    dino: 'Dino',
  },

  days: {
    fri: { name: 'Friday', kind: 'School day' },
    sat: { name: 'Saturday', kind: 'Weekend' },
    sun: { name: 'Sunday', kind: 'Mother’s Day' },
  },

  zones: {
    counsel: 'Counseling room',
    corridor: 'Classrooms',
    yard: 'Courtyard',
    field: 'Sports field',
    home: 'Home',
    schoolGate: 'School grounds',
    street: 'Main street',
    store: 'Corner store',
    park: 'Park',
    neighborhood: 'Neighbourhood',
    classroom: 'Class 8-3',
    library: 'Library',
    artRoom: 'Art & music room',
    lobby: 'Entrance hall',
    cafeteria: 'Cafeteria',
    restroom: 'Restrooms',
  },

  hud: {
    week: (n: number) => `Week ${n}`,
    untilBed: (h: number, mm: string) => `${h}:${mm} to bedtime`,
    energy: 'Energy',
    mood: 'Mood',
    study: 'Study',
    bladder: 'Bladder',
    stars: 'Stars',
    coins: 'Coins',
  },

  tools: {
    english: 'Shadowing',
    radio: 'Radio',
    art: 'Art',
    puzzle: 'Puzzle',
    blocks: 'Blocks',
    social: 'PixelBook',
    party: 'Party',
  },

  side: { todo: 'Today', bag: 'Bag', shop: 'Shop', phone: 'Phone', menu: 'Menu', journal: 'Journal', quests: 'Quests', map: 'Map' },

  common: {
    ok: 'OK',
    close: 'Close',
    back: 'Back',
    gotIt: 'Got it',
    cancel: 'Cancel',
    yes: 'Yes',
    no: 'No',
    minutes: (n: number) => `${n} min`,
    alreadyToday: 'You already did that today — try something else!',
    me: 'Me',
    thoughts: '(Thinking)',
    breathe: 'Deep breath. Let’s make today a good one!',
    looking: (label: string) => `You take a look at the ${label}.`,
    stars: (n: number) => `${n > 0 ? '+' : ''}${n}⭐`,
    coins: (n: number) => `${n > 0 ? '+' : ''}${n}🪙`,
    closedToday: 'Nobody’s here today — school is closed on weekends.',
    schoolClosed: 'The school is closed at the weekend.',
  },

  objectives: {
    title: 'Today’s goals',
    allDone: 'All goals done! Enjoy the rest of the day.',
    bonus: (stars: number) => `All of today’s goals done! Bonus +${stars}⭐`,
    fri: {
      xiaoyu: 'Comfort Xiaoyu in the counseling room',
      teacher: 'Tell Ms. Lin how Xiaoyu is doing',
      log: 'Write the peer-helper log at the desk',
      homework: 'Do your homework at home',
    },
    sat: {
      cookies: 'Bake cranberry cookies at home',
      picnic: 'Picnic with Emma in the courtyard',
      dishes: 'Wash the dishes',
      sister: 'Read a story to your little sister',
    },
    sun: {
      strawberries: 'Buy strawberries at the shop',
      cake: 'Bake a strawberry chiffon cake',
      giveCake: 'Give Mom the cake',
      callDad: 'Call Dad to come home for dinner',
    },
  },

  chores: {
    title: 'Home routine',
    hint: 'Help out at home — Mom notices everything!',
    shoes: 'Shoes and school bag put away',
    dishes: 'Dishes washed',
    sister: 'Story time with your sister',
    homework: 'Homework and shadowing done',
    shower: 'Warm shower, clean pajamas',
    callDad: 'Called Dad to come home for dinner',
    allTitle: '🌟 Model kid of the day 🌟',
    allText: 'You finished the whole home routine! Mom gives you a big hug and some pocket money.',
    allClaim: 'Thanks, Mom! (+20⭐ +50🪙)',
  },

  status: {
    tired: 'You’re exhausted… find a drink or a place to rest.',
    bladderWarn: 'You really need the bathroom!',
    accident: 'Oh no… you didn’t make it to the bathroom in time. How embarrassing.',
    collapseTitle: 'You collapsed!',
    collapseText: 'Everything goes dark… You wake up on the sofa two hours later. Remember to drink and rest!',
    xiaoyuComing: 'Xiaoyu is walking towards the counseling room…',
    xiaoyuSits: 'Xiaoyu pulls out a chair and sits down.',
    familyTime: 'Cozy family time! +mood',
    lateNight: 'It’s getting late. Go to bed at home to end the day.',
  },

  summary: {
    title: (day: string) => `End of ${day}`,
    starsToday: 'Stars earned',
    goals: 'Goals completed',
    chores: 'Home routine',
    collapses: 'Times you collapsed',
    mood: 'Final mood',
    momLove: 'Mom’s affection',
    next: 'Sleep and start the next day',
    weekTitle: (week: number) => `Week ${week} complete!`,
    weekText: (stars: number, rank: string) => `Total stars: ${stars}. Rank: ${rank}.`,
    ranks: ['Getting there', 'Good kid', 'Peer-helper star', 'Campus legend'],
    nextWeek: 'Start next week',
  },

  sleep: {
    label: 'Bed',
    title: 'Your cozy bed',
    early: 'It’s too early to sleep. Maybe a short nap?',
    nap: 'Nap for 30 minutes (+energy)',
    sleep: 'Go to sleep (end the day)',
    stay: 'Not yet',
  },

  menu: {
    title: 'Menu',
    resume: 'Resume',
    language: 'Language',
    sound: (on: boolean) => `Sound: ${on ? 'on' : 'off'}`,
    voice: (on: boolean) => `Voices: ${on ? 'on' : 'off'}`,
    endDay: 'End the day now',
    endDayConfirm: 'Skip to the end of the day?',
    newGame: 'New game',
    save: 'Save game',
    saved: 'Game saved.',
  },

  bag: {
    title: '🎒 My bag',
    use: 'Use',
    empty: 'Your bag is empty.',
    items: {
      tea: { name: 'Tea bag', desc: 'Drink: +12 energy' },
      orange: { name: 'Mandarin orange', desc: 'Eat: +10 energy' },
      candy: { name: 'Fruit throat candy', desc: 'Eat: +4 mood. A nice gift.' },
      cookies: { name: 'Cranberry cookies', desc: 'Eat: +8 energy, +6 mood. Great for sharing.' },
      cake: { name: 'Strawberry chiffon cake', desc: 'A gift from the heart.' },
      strawberries: { name: 'Strawberries', desc: 'For baking a cake.' },
      book: { name: 'Picture book', desc: 'For story time.' },
      water: { name: 'Water bottle', desc: 'Drink: +6 energy (refill at the dispenser)' },
      riceBall: { name: 'Rice ball', desc: 'Eat: +15 energy' },
      juice: { name: 'Juice box', desc: 'Drink: +10 energy' },
      atlas: { name: 'World atlas', desc: 'The library’s missing atlas.' },
      kite: { name: 'Kite', desc: 'Bo’s red kite.' },
      kitten: { name: 'Mochi the kitten', desc: 'Grandma Lee’s kitten. It’s following you!' },
      letter: { name: 'Letter', desc: 'Mom’s letter for Grandma Lee.' },
    },
    used: (name: string) => `You used: ${name}`,
    emptyBottle: 'The bottle is empty — refill it at the water dispenser.',
  },

  shop: {
    title: '🛒 School co-op',
    hint: 'Pay with coins. Earn them by helping at home and solving puzzles.',
    buy: 'Buy',
    bought: (name: string) => `Bought: ${name}`,
    poor: 'Not enough coins!',
  },

  objects: {
    desk: 'Peer-helper desk',
    tea: 'Tea & cocoa bar',
    water: 'Water dispenser',
    shelf: 'Bookshelf',
    plant: 'Potted plant',
    sofa: 'Sofa',
    toilet: 'Restroom',
    easel: 'Art easel',
    chineseDesk: 'Writing desk',
    piano: 'Grand piano',
    board: 'Notice board',
    field: 'Outfield',
    bench: 'Bench',
    shoes: 'Shoe rack',
    sink: 'Kitchen sink',
    oven: 'Oven',
    homeDesk: 'Study desk',
    bath: 'Bathroom',
    bed: 'Bed',
    picnic: 'Picnic blanket',
    atlasDesk: 'Student desk',
    libShelf: 'Library shelf',
    vending: 'Vending machine',
    canteen: 'Lunch counter',
    busStop: 'Bus stop',
    mailbox: 'Mailbox',
    pond: 'Pond',
    bush: 'Bush',
    kiteTree: 'Big tree',
    swing: 'Swing',
    fridge: 'Fridge',
    tv: 'TV',
    shopCounter: 'Counter',
  },

  npc: {
    chunbo: 'Uncle Chun',
    emma: 'Emma',
    teacher: 'Ms. Lin',
    xiaoyu: 'Xiaoyu',
    mom: 'Mom',
    sister: 'Little sister',
    dad: 'Dad',
    librarian: 'Mrs. Wang',
    rain: 'Rain',
    coach: 'Coach Hsu',
    clerk: 'Kai',
    grandma: 'Grandma Lee',
    bo: 'Bo',
    cat: 'Cat',
  },

  desk: {
    text: 'The screen shows the peer-helper schedule and today’s notes.',
    log: 'Write the peer-helper log',
    logDone: 'Log saved: “Xiaoyu (8th grade) often looks tired because of trouble at home. Be patient and kind.”',
    english: 'Open shadowing practice',
    schedule: 'Check today’s goals',
  },

  tea: {
    text: 'The kettle steams softly. It smells of roasted barley and cocoa.',
    drink: 'Make a hot cocoa (+18 energy)',
    drinkToast: 'Warm and sweet — you feel wide awake!',
    share: 'Brew a pot to share (+3 tea bags)',
    shareToast: 'You pack 3 tea bags to give away.',
    watch: 'Watch the drip (+6 mood)',
    watchToast: 'Drip… drip… so relaxing.',
  },

  water: {
    text: 'A clear blue dispenser with hot and cold taps.',
    cold: 'Cold water (+6 energy)',
    coldToast: 'Glug glug — refreshing!',
    hot: 'Hot tea (+8 mood)',
    hotToast: 'A warm cup of tea warms you right up.',
    refill: 'Refill your bottle',
    refillToast: 'Bottle full!',
  },

  shelf: {
    text: 'Rows of colorful books in red, blue, green and gold.',
    read: 'Read “The Psychology of Self-Discipline” (+8 study)',
    readToast: 'Discipline isn’t a cage — it’s how you steer your own life!',
    borrow: 'Borrow a picture book for your sister',
    borrowToast: 'You got a picture book!',
    english: 'Open shadowing practice',
  },

  plant: {
    text: 'Three shades of green leaves in a terracotta pot.',
    water: 'Mist the leaves (+6 mood)',
    waterToast: 'Water droplets sparkle on the leaves.',
    prune: 'Trim old leaves and loosen the soil',
    pruneToast: 'The plant looks healthy and happy!',
  },

  sofa: {
    text: 'A soft beige sofa, perfect for a short break.',
    nap: 'Close your eyes for 20 minutes (+energy, +mood)',
    napToast: 'You feel calm and recharged.',
  },

  toilet: {
    notNeeded: 'You don’t need the bathroom right now.',
    done: 'Ahh, much better!',
  },

  art: {
    title: 'Art room · Easel',
    text: 'The easel lists **the six pillars of visual taste**:\n1. **Shape** — outline and proportion\n2. **Color** — harmony, warm and cool\n3. **Texture** — wood grain, fabric, touch\n4. **Composition** — balance, white space, rhythm\n5. **Structure** — order and support\n6. **Daily practice** — observe → collect → imitate → create',
    quiz: '🎨 Color-mixing quiz',
    quizQ: 'If you mix cobalt blue and lemon yellow in equal parts, what do you get?',
    quizA: ['Fresh grass green', 'Deep violet', 'Bright tangerine'],
    quizRight: 'Correct! Blue + yellow = green.',
    quizWrong: ['That’s blue + red. Try again!', 'That’s red + yellow!'],
    steps: '🖼️ The four-step practice',
    stepsText: '① **Observe** — notice balance, contrast and harmony\n② **Collect** — snap photos of colors and layouts you like\n③ **Imitate** — try those palettes in your room or outfits\n④ **Create** — develop your own style!',
    stepsClaim: 'Got it! (+5⭐)',
  },

  writing: {
    title: 'Writing desk',
    text: 'The blackboard shows sentence patterns for linking ideas.',
    a: '✍️ “Although…, …” sentences',
    aQ: 'Pick the sentence that makes sense:',
    aGood: 'Although it was pouring with rain, we still arrived at school on time and full of energy.',
    aBad: 'Although I ate breakfast, my tummy felt full.',
    aRight: 'Smooth and logical!',
    aWrong: 'The “although” contrast doesn’t quite work there — think again!',
    b: '✍️ “Not only…, but also…” sentences',
    bQ: 'Pick the best sentence:',
    bGood: 'Xiaoyu not only opened up about his worries, but also read an English passage aloud!',
    bBad: 'Xiaoyu not only was tired, but also the sky is blue.',
    bRight: 'A great sentence!',
    bWrong: 'Those two halves don’t build on each other.',
  },

  piano: {
    title: 'Music room · Grand piano',
    text: 'The keys gleam black and ivory in the sunlight.',
    free: '🎹 Play freely',
    freeText: 'Tap a key to play it:',
    played: (note: string) => `♪ ${note}`,
    finish: 'Finish practice (+3⭐)',
    ear: '👂 Ear-training test',
    earQ: 'Which note did you just hear?',
    replay: '🔁 Play it again',
    note: (n: string) => `Note ${n}`,
    right: 'Perfect pitch!',
    wrong: (n: string) => `It was ${n}. Keep practicing!`,
  },

  catchBall: {
    title: 'Outfield fly ball',
    text: (wind: string) => `A high fly ball arcs across the sky! The wind is blowing ${wind}.`,
    winds: { left: 'to the left', right: 'to the right', none: 'hardly at all' },
    left: 'Sprint left',
    right: 'Sprint right',
    stay: 'Stay put and wait',
    caught: 'Great catch! The crowd cheers!',
    missed: 'The ball lands just out of reach. So close!',
  },

  board: {
    title: 'Notice board',
    text: 'Pinned notes: “Peer helpers meet in the counseling room. Be kind, be safe, ask for help.”',
  },

  bench: {
    text: 'A wooden bench under the trees.',
    rest: 'Sit and listen to the birds (+mood)',
    restToast: 'A breeze rustles the leaves. Peaceful.',
  },

  oven: {
    title: 'Kitchen · Oven',
    text: 'The oven glows warm orange. Flour and butter are ready on the counter.',
    cookies: '🍪 Bake cranberry cookies',
    cookiesWork: 'Mixing, scooping, baking…',
    cookiesDone: 'Ding! 3 cranberry cookies are ready!',
    cake: '🍰 Bake a strawberry chiffon cake',
    cakeNeeds: 'You need strawberries first — buy some at the co-op.',
    cakeWork: 'Whipping the meringue, baking, frosting…',
    cakeDone: 'It smells amazing! A strawberry chiffon cake!',
  },

  mom: {
    lines: [
      'Mom ruffles your hair. “Tired? How was school today?”',
      'Mom smiles. “The hallway is so tidy today. You’re growing up!”',
      'Mom is folding laundry. “Anything special you want for dinner?”',
    ],
    giveCake: '🎁 Give Mom the strawberry cake',
    giveCakeNone: 'No cake in your bag yet — bake one in the oven!',
    giveCakeReply: 'Did you really bake this for me? It’s delicious… I’m so happy and so touched!',
    giveCookies: '🎁 Give Mom some cookies',
    giveCookiesNone: 'No cookies in your bag — bake some first!',
    giveCookiesReply: 'So crispy! Thank you, sweetheart.',
    hug: 'Hug Mom: “Thanks for everything. I love you!”',
    hugReply: 'Seeing you healthy, happy and responsible is all I need.',
    tired: 'Mom smiles. “We already had our hug today — go rest, sweetheart.”',
    helpOpt: 'Need any help, Mom?',
    letter: 'Could you take this letter to Grandma Lee? She’s usually in the park, near the pond.',
  },

  shoes: {
    text: 'Home at last. Time to take off your shoes and put your bag away.',
    act: 'Line up your shoes and hang up your bag',
    done: 'Everything’s in its place. Mom noticed!',
  },

  sink: {
    text: 'Lunchboxes and dinner plates are waiting in the sink.',
    act: 'Wash the dishes and lunchboxes',
    done: 'Sparkling clean! Mom says you’re so thoughtful.',
  },

  sister: {
    text: 'Your little sister runs over with her toys. “You’re home!”',
    read: 'Read her a picture book',
    readNoBook: 'You have no picture book. Borrow one from the counseling room bookshelf.',
    readDone: 'She giggles all the way through the story. Mom smiles from the doorway.',
    puzzle: 'Play a sliding puzzle together',
  },

  homeDesk: {
    text: 'A tidy desk and a soft lamp.',
    act: 'Do your homework, then some shadowing',
    done: 'Homework done! Mom is proud of you.',
  },

  bath: {
    text: 'Steam rises from the warm bath.',
    shower: 'Shower and change into clean pajamas',
    showerDone: 'Fresh, clean and cozy!',
    toilet: 'Use the toilet',
  },

  dad: {
    hello: 'Dad loosens his tie. “I made it home for dinner, just like I promised!”',
    hug: 'Give Dad a hug',
    hugReply: 'That’s the best welcome home.',
  },

  xiaoyu: {
    title: 'Xiaoyu · 8th grade',
    open: '…I really don’t want to go to class today.',
    sitDown: 'Sit with me for a bit. Want some hot cocoa?',
    tired: '…My parents were fighting again until late. I’m so tired. And scared.',
    safe: 'You’re safe here. Stay as long as you need — I’ll stay with you.',
    english: 'Want to read some English together to take your mind off it?',
    better: '…Thanks. I feel a bit better. I’ll try to go back for second period.',
    promise: 'Let’s meet again after lunch, okay?',
    done: 'Xiaoyu trusts you now.',
    candy: 'Offer him a throat candy',
    candyNone: 'You don’t have any throat candy.',
    candyDone: 'Xiaoyu takes the candy and nods.',
    later: 'Xiaoyu gives you a small wave on his way to class.',
    satHello: '…Oh, hi. I didn’t want to stay home today. I’m just sitting here.',
    satInvite: 'Emma and I are having a picnic. Want to come?',
    satAccept: 'Really? Only if Emma says it’s okay…',
    satJoined: 'This is the first time in a long while I’ve had fun at the weekend. Thanks.',
    askEmma: 'Can Xiaoyu join our picnic?',
    emmaYes: 'Of course! The more the merrier. I’ll save him a sandwich!',
  },

  teacher: {
    before: 'Have you seen Xiaoyu? He didn’t come to class this morning. I’m worried.',
    report: 'Tell her Xiaoyu is calm now and will be back for second period',
    thanks: 'Thank you so much. Having peer helpers like you makes such a difference.',
    after: 'Thanks again for looking after Xiaoyu today.',
  },

  chunbo: {
    title: 'Uncle Chun · School guard',
    versions: [
      {
        intro: 'Good morning, young one! Everything’s quiet on my rounds. Come have some tea later!',
        give: 'Morning, Uncle Chun! Have something warm',
        learn: 'Ask for safety tips around school',
      },
      {
        intro: 'A stranger was handing out flyers at the gate earlier. I sent him off. Stay safe out there!',
        give: 'Thanks for looking after us! Have a treat',
        learn: 'Ask how to deal with strangers',
      },
      {
        intro: 'What a lovely day! Seeing you kids full of energy makes me feel young again.',
        give: 'You look great today! Have a little gift',
        learn: 'Chat about the flowers in the courtyard',
      },
    ],
    gotTea: 'Uncle Chun sips the tea and beams!',
    gotCandy: 'Uncle Chun pops the candy in his mouth. “Ah, that soothes my throat!”',
    gotNothing: 'You give Uncle Chun a cheerful wave.',
    hungryOpt: 'You look tired, Uncle Chun. Everything okay?',
    hungry: 'Haha, you noticed! I skipped breakfast and I can’t leave the gate. My stomach is growling…',
    riceAccept: 'I’ll get you a rice ball from the store!',
    riceGive: 'Give Uncle Chun the rice ball 🍙',
    riceThanks: 'Ah, still warm! You’re a lifesaver. Come by the booth anytime!',
    tipTitle: 'Uncle Chun’s advice',
    tip: 'Don’t chat with strangers who approach you, and never take snacks or drinks from people you don’t know. Safety first!',
    remember: 'I’ll remember!',
  },

  emma: {
    lines: [
      'Hey! Want to practice English pronunciation together?',
      'Hi! Did you listen to the English radio today? The music was awesome!',
      'Good morning! Have you finished your homework? We can study together!',
    ],
    picnicLine: 'You came! I brought a blanket and juice. Did you bring anything to share?',
    english: 'Sure! Let’s do shadowing',
    radio: 'Let’s listen to the radio',
    cookies: 'Give her some cookies',
    cookiesNone: 'No cookies in your bag — bake some at home first!',
    cookiesReply: 'Wow, homemade cookies! They smell incredible! Thank you!',
    picnicShare: 'Share your homemade cookies',
    picnicNone: 'You have nothing to share yet… bake cookies at home first!',
    picnicTitle: '🧺 Weekend picnic',
    picnicDone: 'You and Emma share cookies on the grass while music plays. Perfect afternoon!',
    absent: 'Emma isn’t around right now.',
  },

  calls: {
    incoming: 'Incoming call',
    answer: 'Answer',
    decline: 'Decline',
    declined: 'Call declined.',
    pool: {
      scamStock: {
        caller: 'Unknown · “Investment Advisors”',
        desc: '0982-xxx-xxx (sales call)',
        intro: 'Hello! We’re a top investment firm. 30% profit every week, guaranteed! Add us on chat now for free stock tips!',
        good: 'Hang up and report it to the 165 anti-fraud hotline',
        goodMsg: 'Sharp! That’s a classic investment scam.',
        bad: '“Really? No risk at all?”',
        badMsg: 'Careful! Huge promised returns are a red flag — you almost gave away your account.',
      },
      scamGame: {
        caller: 'Suspicious · “Game Support”',
        desc: 'Claims you won a free legendary skin',
        intro: 'Congratulations, player! You won a limited legendary skin! Just tell us your password and the SMS code so we can add it for you!',
        good: 'Refuse and hang up',
        goodMsg: 'Exactly! Real staff never ask for passwords or codes.',
        bad: 'Read out the SMS code',
        badMsg: 'Oh no! Your account was stolen right after you gave the code.',
      },
      insurance: {
        caller: 'Sales · “Safe Life Insurance”',
        desc: 'Selling a savings plan',
        intro: 'Hi there! Got two minutes? Our new savings insurance pays great dividends, and you get a free gift!',
        good: '“Thanks, but I’m underage. I’ll talk to my parents.”',
        goodMsg: 'Polite and sensible! Money decisions are for the whole family.',
        bad: 'Shout at them and hang up',
        badMsg: 'A bit hot-headed… your mood dips.',
      },
      mom: {
        caller: 'Mom ❤️',
        desc: 'Just checking in',
        intro: 'Sweetheart, it got chilly — did you bring a jacket? Did you eat enough at lunch?',
        good: '“Yes, Mom! I’m warm and full. Take care of yourself too!”',
        goodMsg: 'Mom’s heart melts. You feel the warmth of home.',
        bad: '“Yeah yeah, I’m busy, bye.”',
        badMsg: 'Mom sighs softly on the other end…',
      },
      friend: {
        caller: 'Best friend Jay 🏀',
        desc: 'Calling about after school',
        intro: 'Hey! The gym is free after school! Want to practice three-pointers? I’ve got a new ball!',
        good: '“Sure! See you on the court!”',
        goodMsg: 'Friends forever! You’re fired up!',
        bad: '“I’ll finish my peer-helper tasks first. Tomorrow?”',
        badMsg: 'Responsible choice! Jay says tomorrow works.',
      },
    },
  },

  phone: {
    tabs: { chats: '💬 Chats', calls: '📞 Calls', feed: '🌐 Feed', radio: '📻 Radio' },
    chatsTitle: 'Chats',
    placeholder: 'Type a message…',
    now: 'now',
    yesterday: 'Yesterday',
    send: 'Send',
    stickers: 'Stickers',
    defaultReply: 'Got your message! Have a great day!',
    chats: {
      family: {
        name: 'Family (4)',
        preview: 'Dad sent a good-morning card',
        msgs: [
          { from: 'Dad', text: '@card' },
          { from: 'Mom', text: 'Stay safe out there, everyone. Have a good day!' },
          { from: 'Little sister', text: 'Good morning! Can we play when you get home?' },
        ],
        choices: [
          'Reply: Morning! I’ll be home on time for dinner.',
          'Send a good-morning card 🌸',
          'Reply to sis: Wait for me — I’ll read you a story and we’ll do a puzzle!',
        ],
        r1: [
          { from: 'Mom', text: 'Lovely! I’ll make your favorite dish.' },
          { from: 'Dad', text: 'I’ll be home on time too!' },
        ],
        r1more: 'Yay, can’t wait for dinner!',
        cardReply: { from: 'Dad', text: 'Haha, what a pretty card! Have an energetic day!' },
        r3: [
          { from: 'Little sister', text: 'Yay! You’re the best! 🎉' },
          { from: 'Mom', text: 'You two are so sweet.' },
        ],
        replies: ['Got it! Stay safe and healthy, everyone!', 'Have a happy day, all of you!'],
      },
      friends: {
        name: 'Best friends (3)',
        preview: 'Jay: picnic or basketball this weekend?',
        msgs: [
          { from: 'Jay', text: 'So — picnic or basketball this weekend? 🏀' },
          { from: 'Rain', text: 'Picnic! Who’s bringing snacks? 🍰' },
          { from: 'Ming', text: 'Don’t forget to check on Xiaoyu from class 8-3!' },
        ],
        choices: [
          'To Rain: I’ll bake cranberry cookies for the picnic!',
          'To Ming: Xiaoyu is settled in the counseling room, don’t worry.',
          'To Jay: Picnic first, then 3-on-3!',
        ],
        r1: [
          { from: 'Rain', text: 'Yesss! You’re a baking genius! 🍰' },
          { from: 'Jay', text: 'Eat first, then ball. Perfect plan!' },
        ],
        r2: { from: 'Ming', text: 'Thank you! It’s great to have you there.' },
        r3: { from: 'Jay', text: 'Deal! Best weekend ever.' },
      },
      mom: {
        name: 'Mom ❤️',
        preview: 'Remember to drink water and eat breakfast',
        msgs: [
          { from: '', text: 'It’s cold — wear your jacket and eat a proper lunch.' },
          { from: '', text: 'If you’ll be late, send me a message.' },
        ],
        choices: ['Reply: OK Mom ❤️ I’ll eat well and come home on time!', 'Send a good-morning card 🌸'],
        r1: 'Good kid! Be careful on the way.',
        replyMorning: 'Morning! Drink some warm water and have breakfast.',
        replyLove: 'Love you too! Come home early tonight ❤️',
        replyOther: 'Okay, got it. Take care of yourself at school!',
        kwMorning: ['morning', 'breakfast', 'eat', 'food'],
        kwLove: ['love', '❤'],
      },
      emma: {
        name: 'Emma',
        preview: 'Shadowing practice?',
        msgs: [{ from: '', text: 'Hey! Ready for some shadowing practice?' }],
        choices: ['Yes! Let’s practice right now.', 'Can you give me a hard word?'],
        r1me: 'Yes! Opening the shadowing notebook now!',
        r1: 'Awesome! Let’s aim for perfect! 💪',
        r2me: 'Can you give me a challenging word to practice?',
        r2: 'Try “responsibility” and “resilient”! 🌟',
        replies: ['Got it! Let’s keep learning!', 'Awesome! Practice makes perfect!'],
      },
    },
    card: [
      { top: '🌸😊🌸', big: 'Good morning', sub: 'Start the day with a smile\nWishing you health and luck' },
      { top: '☀️🌻✨', big: 'Peace & joy', sub: 'May everything go your way\nFull of energy today!' },
    ],
    stickerLines: ['🌸 Have a lovely day!', '☀️ Sunshine for you!', '🌈 Good vibes!', '❤️ Love you all', '💪 We’ve got this!', '👍 Sounds good!', '😂 Haha!', '🙏 Thank you!'],
    callList: {
      title: '📞 Contacts',
      mom: { name: 'Mom', sub: 'Family call' },
      dad: { name: 'Dad', sub: 'Ask him to come home for dinner' },
      friend: { name: 'Best friend Jay', sub: 'Weekend plans' },
      hotline: { name: '165 Anti-Fraud Hotline', sub: 'Check if something is a scam' },
    },
    call: {
      connecting: 'Connecting…',
      inCall: 'In call',
      hangUp: '🔴 Hang up',
      mom: {
        line: 'Hello, sweetheart? Be careful on the way home — I’m making your favorite dinner!',
        a: '“Mom, I’m already on my way!”',
        b: '“Love you, Mom! I’ll help set the table and wash up!”',
        ra: 'Okay, see you soon. Walk carefully!',
        rb: 'What a thoughtful kid! I’m so proud.',
      },
      dad: {
        line: 'Hello? I just got out of a meeting. What’s up?',
        a: '“Dad, come home on time and have dinner with us!”',
        b: '“Don’t work too hard, Dad. Come home early!”',
        reply: 'Since you asked so nicely, I’m packing up and coming home for dinner!',
      },
      friend: {
        line: 'Hey! We’re doing a picnic and basketball this weekend. Bringing your famous snacks?',
        a: '“Of course! Cranberry cookies coming up!”',
        b: '“See you at the court!”',
        reply: 'Awesome! Everyone’s waiting in the group chat!',
      },
      hotline: {
        line: 'This is the 165 anti-fraud hotline. Unexpected charges, or strangers asking for passwords, are always scams!',
        a: '“Got it, I’ll stay alert. Thank you, officer!”',
        reply: 'Great fraud awareness! Have a safe day!',
      },
    },
    feed: {
      brand: 'PixelBook',
      tagline: 'Stay safe online',
      post: '📷 Post a photo',
      rules: '🛡️ Safety rules',
      report: '🔒 Report & block',
      interact: 'Click it',
      reported: '✅ Reported and blocked',
      like: '👍 Like',
      liked: '❤️ Liked',
      comment: '💬 Comment',
      commented: 'You replied to Rain!',
      reportOk: 'Right call! You protected yourself and your friends.',
      reportBad: 'Risky! Clicking suspicious links is how people get scammed.',
      likeOn: 'Liked!',
      likeOff: 'Like removed',
      posts: {
        scam1: {
          author: 'Stranger “GameTycoon”',
          time: '5 min ago',
          tag: '⚠️ Stranger friend request',
          content: 'Hi! Add me and I’ll give you 5000 game points for free — just DM me a screenshot of your login!',
        },
        scam2: {
          author: 'Group “FREE PHONES”',
          time: '25 min ago',
          tag: '⚠️ Phishing',
          content: 'Limited offer! Click the link and enter your parent’s phone and card number to win a free phone!',
        },
        rain: {
          author: 'Best friend Rain 📚',
          time: '1 h ago',
          tag: '🌿 School life',
          content: 'Reviewed English vocab at the library today — so productive! Picnic this weekend, anyone? 🥪',
        },
        mine: {
          author: 'Me',
          time: 'just now',
          tag: '🍰 Baking',
          content: 'Baked a strawberry chiffon cake after school! The whole family loved it~',
        },
      },
      photoTitle: '📷 New photo post',
      photoText: 'What do you want to share? (Think about privacy!)',
      photoCake: '🍰 My fresh-baked cake',
      photoCakeDone: 'Posted! Your friends are liking it.',
      photoDoor: '🏠 My front door with the house number',
      warnTitle: '⚠️ Safety warning!',
      warnText: 'Never post photos showing your house number, building entrance or keys! Strangers could use them to find where you live.',
      warnFix: 'Hide the house number and post a school photo instead',
      warnFixed: 'Well done — you protected your address!',
      rulesTitle: '🛡️ Online safety rules',
      rulesText: '**1. Don’t accept strangers.** Online profiles can be fake.\n\n**2. Keep personal info private.** Your full name, phone, address and school class never go online.\n\n**3. Passwords are secret.** Anyone offering free items for your password or code is a scammer.\n\n**4. If it feels wrong, get help.** Tell a parent or teacher right away.',
      rulesClaim: 'I’ve memorized them! (+5⭐)',
    },
    radio: {
      title: '📻 Learning radio',
      text: 'Pick something to listen to (opens YouTube):',
      a: { name: 'mebook888 daily English', sub: 'Conversation and listening' },
      b: { name: 'English pop playlist', sub: 'Upbeat songs to hum along' },
      c: { name: 'Kids’ science in English', sub: 'Learn science and English' },
      opened: 'Opened in a new tab.',
    },
  },

  english: {
    title: '📚 English shadowing',
    hint: '🔊 Listen → 🎤 Repeat → ✅ Earn stars',
    listen: '🔊 Listen',
    practice: '🎤 Practice',
    sayIt: (w: string) => `Say it clearly and out loud: **${w}**`,
    listening: '🎤 Listening… say the word now',
    heard: (s: string) => `I heard: “${s}”`,
    match: 'Great pronunciation!',
    noMatch: 'Not quite — listen again and retry.',
    selfCheck: '✅ I said it clearly',
    tryMic: '🎤 Check with microphone',
    again: '🔁 Listen again',
    noMic: 'Microphone not available — use the self-check button.',
    meanings: {
      responsibility: 'responsibility',
      empathize: 'to understand others’ feelings',
      discipline: 'self-control, discipline',
      resilient: 'able to bounce back',
      boundary: 'a personal limit',
      procrastinate: 'to put things off',
      cyberbullying: 'bullying online',
    },
  },

  blocks: {
    title: '🧱 Block stack',
    score: 'Score',
    lines: 'Lines',
    level: 'Level',
    next: 'Next',
    restart: 'Restart',
    quit: 'Exit',
    over: 'Game over',
    cleared: (n: number) => `${n} line${n > 1 ? 's' : ''} cleared!`,
    quad: '💥 Four lines at once!',
    capped: 'Daily star limit for this game reached — play just for fun!',
    final: (score: number) => `Final score: ${score}`,
  },

  puzzle: {
    title: '🧩 Sliding puzzle',
    hint: 'Tap a tile next to the gap. Order 1–8!',
    shuffle: 'Shuffle',
    moves: (n: number) => `Moves: ${n}`,
    solved: '🎉 Puzzle solved!',
  },

  radio: {
    title: '📻 English radio',
  },

  party: {
    title: '🎂 Birthday parties',
    text: 'Whose birthday are we celebrating? (Each one needs a cake!)',
    me: (d: string) => `🎉 My birthday (${d})`,
    mom: (d: string) => `💐 Mom’s birthday (${d})`,
    dad: (d: string) => `👔 Dad’s birthday (${d})`,
    emma: (d: string) => `👭 Emma’s birthday (${d})`,
    date: (m: number, d: number) => `${m}/${d}`,
    needCake: 'You need a cake for the party — bake one in the oven!',
    done: 'You already celebrated this birthday!',
    meTitle: '🎂 My birthday party',
    meText: 'Mom, Dad, your sister, Emma and Jay are all here! A strawberry chiffon cake with lit candles sits on the table and everyone sings for you. 🎶',
    meWish: '🌟 Close your eyes and make a wish',
    meBlow: 'You blow out the candles and everyone cheers! Mom hands you a gift, Dad ruffles your hair, and your sister grabs the first strawberry.',
    meThanks: 'Thank you, everyone! 💖',
    meVoice: 'Happy birthday, sweetheart! Make a wish!',
    momTitle: '💐 Mom’s birthday',
    momText: 'You secretly baked a strawberry chiffon cake and lit the candles. Everyone sings and gives Mom a handwritten card: “Thank you for always taking care of us!”',
    momAct: 'Feed Mom the first slice',
    momVoice: 'Thank you, sweetheart! This is the best present ever!',
    momDone: 'Mom tears up and gives you a long hug.',
    dadTitle: '👔 Dad’s birthday',
    dadText: 'Dad came home on time! The table is full of home cooking, everyone’s wearing party hats, and there’s your homemade cake.',
    dadAct: '“Happy birthday, Dad! Thanks for working so hard!”',
    dadVoice: 'Haha! Thank you! Every busy day is worth it for this!',
    dadDone: 'Dad laughs out loud. The family has never felt closer!',
    emmaTitle: '👭 Emma’s birthday picnic',
    emmaText: 'Sunshine on the grass, a blanket, homemade treats, and everyone celebrating Emma!',
    emmaAct: 'Give her the cake and a card',
    emmaVoice: 'Thank you so much! You’re my best friend!',
    emmaDone: 'Emma is overjoyed!',
  },

  map: {
    names: { town: 'Town', school: 'School', home: 'Home', store: 'Corner store' },
    you: 'You',
    goal: 'Goal',
    people: 'People',
    doors: 'Doors',
  },

  quests: {
    title: 'Quests',
    started: 'New quest',
    completed: 'Quest complete',
    active: 'Active',
    available: 'Available',
    done: 'Done',
    track: 'Follow',
    tracking: 'Following',
    none: 'No quests yet. Talk to people around town!',
    clover: (n: number, total: number) => `🍀 Lucky clover ${n}/${total}`,
    lapsDone: (secs: number) => `🏁 All flags in ${secs}s! Tell the coach.`,
    lapsFail: '⏱ Time’s up! Talk to Coach Hsu to try again.',
    list: {
      atlas: {
        title: 'The missing atlas',
        desc: 'Mrs. Wang’s big world atlas has vanished from the library.',
        steps: ['Talk to Mrs. Wang in the library', 'Look for the atlas in class 8-3', 'Return the atlas to Mrs. Wang'],
      },
      riceBall: {
        title: 'A hungry guard',
        desc: 'Uncle Chun skipped breakfast to watch the gate.',
        steps: ['Talk to Uncle Chun at the school gate', 'Buy a rice ball at the corner store', 'Bring the rice ball to Uncle Chun'],
      },
      laps: {
        title: 'Beat the clock',
        desc: 'Coach Hsu challenges you to run past every flag on the track in under a minute.',
        steps: ['Talk to Coach Hsu on the sports field', 'Run through the 4 flags in order', 'Tell Coach Hsu you made it'],
      },
      study: {
        title: 'Study buddies',
        desc: 'Rain wants to practice English vocabulary together.',
        steps: ['Talk to Rain in the library', 'Practice 3 different words (shadowing)', 'Tell Rain how it went'],
      },
      kitten: {
        title: 'Where’s Mochi?',
        desc: 'Grandma Lee’s kitten ran off into the park.',
        steps: ['Talk to Grandma Lee in the park', 'Search the bushes in the park — listen for rustling', 'Bring Mochi back to Grandma Lee'],
      },
      kite: {
        title: 'Kite in the tree',
        desc: 'Bo’s kite is stuck in the big tree in the neighbourhood.',
        steps: ['Talk to Bo in the neighbourhood', 'Shake the big tree (three times!)', 'Give the kite back to Bo'],
      },
      xiaoyuPicnic: {
        title: 'A seat at the picnic',
        desc: 'Xiaoyu is sitting alone in the park on Saturday.',
        steps: ['Talk to Xiaoyu in the park', 'Ask Emma if Xiaoyu can join the picnic', 'Ask Emma if Xiaoyu can join the picnic'],
      },
      letter: {
        title: 'Special delivery',
        desc: 'Mom has a letter for Grandma Lee.',
        steps: ['Ask Mom if she needs help', 'Give the letter to Grandma Lee in the park', 'Give the letter to Grandma Lee in the park'],
      },
      clovers: {
        title: 'Lucky clovers',
        desc: 'Four-leaf clovers are hidden around town, at school and at home.',
        steps: ['Keep your eyes open for sparkling clovers', 'Find all the lucky clovers', 'Find all the lucky clovers'],
      },
      postcards: {
        title: 'Postcards for Grandma',
        desc: 'Grandma Lee never saw the big city. Bring her postcards from three Taipei landmarks.',
        steps: ['Talk to Grandma Lee in the park', 'Collect postcards at Taipei Main Station, Longshan Temple and Liberty Square', 'Give the postcards to Grandma Lee'],
      },
      pineapple: {
        title: 'Pineapple cakes from Taipei',
        desc: 'Mom wants a box of real Taipei pineapple cakes for Grandma’s tea.',
        steps: ['Talk to Mom at home', 'Buy pineapple cakes from the shop clerk in Taipei', 'Give the pineapple cakes to Mom'],
      },
      elephant: {
        title: 'The photo from Elephant Mountain',
        desc: 'Emma wants a photo of Taipei 101 taken from the top of Elephant Mountain.',
        steps: ['Talk to Emma at home', 'Ask the hiker at the Elephant Mountain viewpoint for a photo', 'Show Emma the photo'],
      },
      mochi: {
        title: 'Hualien mochi',
        desc: 'Bo will trade his best marble for a box of soft Hualien mochi.',
        steps: ['Talk to Bo in the neighbourhood', 'Buy mochi from the mochi auntie in Hualien', 'Give the mochi to Bo'],
      },
      pebble: {
        title: 'A pebble from Qixingtan',
        desc: 'Rain’s stone collection is missing a pebble from Qixingtan beach.',
        steps: ['Talk to Rain in the library', 'Ask the fisherman at Qixingtan beach for a pebble', 'Give the pebble to Rain'],
      },
    },
  },

  library: {
    hello: 'Shh… welcome to the library. Take your time.',
    ask: 'Oh dear. Our big world atlas is missing — I think a student borrowed it for geography and forgot it in a classroom. Could you look for it?',
    accept: 'Sure, I’ll find it!',
    hint: 'Class 8-3 had geography this morning. Try their desks!',
    thanks: 'You found it! Thank you. Here — a little something for the library’s best helper.',
    found: 'Under the desk, half hidden by a school bag… the world atlas!',
    deskNothing: 'A student desk with doodles carved into the corner.',
    shelfText: 'Encyclopedias, comics, poetry… the shelves smell of old paper.',
    read: 'Read for a while (+10 study)',
    readToast: 'You learn something new about octopuses. They have three hearts!',
  },

  vending: { text: 'Bright drinks behind glass. It hums softly.', buy: 'Buy a juice box', bought: 'Clunk! A juice box drops out.' },
  canteen: {
    text: 'Today’s lunch: rice, braised tofu, greens and soup.',
    closed: 'The lunch counter opens from 11:30 to 13:30.',
    lunch: 'Have lunch (+30 energy)',
    lunchDone: 'Warm and filling. You feel ready for the afternoon!',
  },
  busStop: {
    text: 'Line 8 — to the train station. The next bus is in 12 minutes.',
    where: 'Line 8 stops here. Where would you like to go? (Ask in English: “Does this bus go to the mall?”)',
    stops: { busStopW: 'West — store, park & home', busStop: 'School', busStopE: 'East — sports centre & mall' },
    here: '📍 You are here',
    notNow: 'Not now',
    beep: '🚌 Beep! You tap your EasyCard and hop on.',
    arrived: (name: string) => `🚏 ${name}. “Thank you, driver!”`,
    night: 'The last bus has gone (buses run 6:00–22:00). Time to walk!',
  },
  mailbox: {
    postcard: 'A postcard from Grandpa! “Saw the sea today. Be good, study hard, and eat your vegetables.”',
    empty: 'The mailbox is empty.',
  },
  pond: { text: 'Orange fish glide under the lily pads.', feed: 'Feed the fish (+mood)', feedToast: 'The fish gather in a swirl of orange!' },
  bush: { text: 'A thick, leafy bush.' },
  kitten: {
    found: 'Something white is curled up in the leaves… it’s Mochi! The kitten mews and follows you.',
    notHere: 'You part the leaves… nothing but a startled beetle.',
  },
  kite: {
    hello: 'My kite flies the highest in the whole street!',
    ask: 'My kite got stuck in the big tree! I’m too short to reach it… can you help?',
    accept: 'Leave it to me!',
    waiting: 'Shake the tree hard! It’s the big one by the path.',
    thanks: 'My kite! You’re the best! Here, have some candy!',
    after: 'Look how high it goes!',
    treeText: 'A big old tree with a thick trunk.',
    treeAfter: 'The branches sway gently. No more kites up there.',
    shakeMore: (n: number) => `The branches shake… the kite wobbles! (${n} more)`,
    fell: 'With one last shake, the kite flutters down into your hands!',
  },
  swing: { text: 'A swing set, its chains warm from the sun.', play: 'Swing for a bit', toast: 'Wheee! Higher and higher!' },
  fridge: { text: 'Leftovers, fruit, and a pudding with your name on it.', snack: 'Have a snack (+12 energy)', snackToast: 'Yum! Mom’s pudding is the best.' },
  tv: { text: 'A cooking show is on.', watch: 'Watch for a while', toast: 'You learn how to fold dumplings. Sort of.' },
  rain: {
    hello: 'Hey! The library is my favorite place.',
    offer: 'Want to be study buddies? Practice three English words, then tell me how it went!',
    accept: 'Deal!',
    waiting: (n: number) => `How’s it going? You’ve practiced ${n}/3 words today.`,
    done: 'Amazing! Your pronunciation is getting so good!',
    sat: 'Saturday in the park! Did you see the fish in the pond?',
  },
  coach: {
    hello: 'Keep your knees up, everyone!',
    offer: 'You look fast! Run past all four flags on the track, in order, in under a minute. Ready?',
    retry: 'Want another go? Four flags, one minute!',
    accept: 'Ready!',
    go: '🏁 Go! Follow the flags!',
    running: 'Go go go! Follow the flags!',
    done: 'Great run! That’s the spirit!',
    after: 'Nice work today. Remember to stretch!',
  },
  clerk: {
    lines: ['Welcome! Fresh rice balls just came in.', 'Hi again! Need anything?', 'Strawberries are in season — very sweet!'],
    shop: '🛒 Let’s shop',
  },
  grandma: {
    lines: ['The pigeons know me by now. Coo, coo!', 'What a lovely day for a walk.', 'You remind me of my grandson.'],
    kittenAsk: 'Oh, dear… my kitten Mochi slipped out of her basket and ran into the bushes. My knees aren’t what they used to be…',
    kittenAccept: 'I’ll find Mochi for you!',
    kittenWaiting: 'Listen carefully — if a bush is rustling, Mochi might be inside.',
    kittenThanks: 'Mochi! Oh, thank you, thank you! Here, take this for your trouble.',
    letterThanks: 'A letter from your mother? How thoughtful. Tell her I’ll bring dumplings next week!',
  },
  cat: { lines: ['Meow~ 🐱', 'Purrrr…', 'The cat rubs against your leg.', 'The cat blinks slowly at you. That means “I like you”.'] },

  talks: enTalks,
  ...enExtra,
  ...enPlaces,
  ...enArcade,
  ...enTreasure,
  ...enTalks3,
  ...enGuide,
  ...enWorld3,
  ...enTrips,
  ...enSky,
  ...enFamily,
  ...enClassroom,
  ...enLife,
  ...enXinyi,
  ...enXimen,
  ...enCks,
  ...enHlcity,
  ...enTaroko,
  ...enStory,
  ...enAirport,
  ...enKaohsiung,
  ...enTainan,
  ...enBarcelona,

  rewards: {
    stars: (n: number) => `+${n}⭐`,
    energy: (n: number) => `+${n}⚡`,
    mood: (n: number) => `+${n}💚`,
    study: (n: number) => `+${n}📄`,
    coins: (n: number) => `+${n}🪙`,
    love: (n: number) => `+${n}💖`,
    got: (what: string) => `Got ${what}`,
  },
};

type Widen<T> = T extends string
  ? string
  : T extends (...args: infer A) => string
    ? (...args: A) => string
    : T extends readonly (infer U)[]
      ? Widen<U>[]
      : T extends object
        ? { [K in keyof T]: Widen<T[K]> }
        : T;

export type Messages = Widen<typeof en>;
